import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { AgSpinner } from './components/ag-ui';
import { store } from './app/store';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { AnagogiaThemeProvider } from './theme';
import ShellSwitch from './components/mobile/ShellSwitch';
import { loginRequest, msalConfig, b2cPolicies, b2cPolicyConfig } from './authConfig';
import { isForgotPasswordError, isCancelledByUser, isPasswordResetResponse } from './utils/b2cRedirect';
import { FACADE, NOM_DU_PRODUIT } from './facade/resolveFacade';
import { retenirDossierAvantConnexion } from './components/navigation/dossierParam';

// ✨ Code Splitting: Lazy loading des composants lourds
// C24 SP5 : l'accueil (vidéo de présentation ou scène animée) est servi sur /login. L'ancienne
// page d'accueil (Login, clips, bande « pourquoi », lien d'exemple), gardée un temps sur
// /decouvrir, a été retirée le 31/10/2026 au soir (décision du propriétaire).
const Accueil = lazy(() => import('./pages/Accueil'));
// Sous la façade Azaria (C26 SP3), la page de connexion est la sienne : rien d'Anagogia avant d'entrer.
const ConnexionAzaria = lazy(() => import('./facade/azaria/ConnexionAzaria'));
const PageDeConnexion = FACADE.facade === 'azaria' ? ConnexionAzaria : Accueil;

// Composant de chargement élégant pour Suspense
const PageLoader: React.FC<{ message?: string }> = ({ message = 'Chargement...' }) => (
    <div className="flex flex-col justify-center items-center h-screen gap-2" style={{ backgroundColor: 'var(--ag-background)' }}>
        <AgSpinner size="lg" />
        <p className="text-body text-secondary">{message}</p>
    </div>
);

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Vérifier si l'authentification est désactivée pour les tests
    const isAuthDisabled = process.env.REACT_APP_DISABLE_AUTH_FOR_TESTS === 'true' || process.env.NODE_ENV === 'test';

    // Hooks doivent être appelés inconditionnellement au début
    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();
    const [isAuthChecked, setIsAuthChecked] = useState(false);

    // Force la vérification de l'auth à chaque montage
    useEffect(() => {
        if (isAuthDisabled) {
            setIsAuthChecked(true);
            return;
        }

        const checkAuth = async () => {
            try {
                await instance.initialize();
                const accounts = instance.getAllAccounts();
                if (accounts.length > 0) {
                    instance.setActiveAccount(accounts[0]);
                }
                setIsAuthChecked(true);
            } catch (error) {
                console.error('Erreur lors de la vérification auth:', error);
                setIsAuthChecked(true);
            }
        };

        checkAuth();
    }, [instance, isAuthDisabled]);

    // Mode test : bypass l'authentification
    if (isAuthDisabled) {
        return <>{children}</>;
    }

    // Attendre que la vérification soit terminée
    if (!isAuthChecked) {
        return <div>Vérification de l'authentification...</div>;
    }

    // Si des comptes MSAL existent, considérer comme authentifié
    const accounts = instance.getAllAccounts();
    const hasValidAuth = isAuthenticated || accounts.length > 0;

    if (!hasValidAuth) {
        // « Mes notes » depuis la façade : le dossier demandé survit au passage par la connexion (fumée SP4.13).
        retenirDossierAvantConnexion(window.location.search);
        return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
};

const App: React.FC = () => {
    const { instance } = useMsal();
    const [isInitialized, setIsInitialized] = useState(false);
    
    useEffect(() => {
        // Le titre de l'onglet suit le produit de l'hôte (index.html dit « Anagogia »).
        document.title = NOM_DU_PRODUIT;
        // Force la définition du compte actif au démarrage
        const handleAuth = async () => {
            try {
                // Attendre que MSAL soit initialisé
                await instance.initialize();
                
                // IMPORTANT : Ne gérer handleRedirectPromise qu'UNE SEULE FOIS au démarrage
                const response = await instance.handleRedirectPromise();
                if (response && isPasswordResetResponse(response.idTokenClaims as Record<string, unknown>, b2cPolicies.forgotPassword)) {
                    // Mot de passe réinitialisé : le jeton vient du flux de
                    // réinitialisation, pas de celui de connexion — on reconnecte.
                    await instance.loginRedirect({ ...loginRequest, redirectUri: msalConfig.redirectUri, extraQueryParameters: { ui_locales: 'fr' } });
                    return;
                } else if (response) {
                    console.log('[AUTH] Retour de redirection détecté:', response.account?.username);
                    instance.setActiveAccount(response.account);
                } else {
                    // Pas de redirection, vérifier les comptes existants
                    const accounts = instance.getAllAccounts();
                    if (accounts.length > 0) {
                        instance.setActiveAccount(accounts[0]);
                        console.log('[AUTH] Compte actif défini:', accounts[0].username);
                    }
                }
                
                setIsInitialized(true);
            } catch (error) {
                if (isForgotPasswordError(error)) {
                    // « Vous avez oublié votre mot de passe ? » : B2C rend la main
                    // avec AADB2C90118 ; on ouvre le flux de réinitialisation.
                    await instance.loginRedirect({
                        ...loginRequest,
                        authority: b2cPolicyConfig.authorities.forgotPassword.authority,
                        redirectUri: msalConfig.redirectUri,
                        extraQueryParameters: { ui_locales: 'fr' },
                    });
                    return;
                }
                if (!isCancelledByUser(error)) {
                    console.error('[AUTH] Erreur lors du traitement de l\'authentification:', error);
                }
                setIsInitialized(true);
            }
        };
        
        handleAuth();
    }, []); // Dépendances vides pour n'exécuter qu'une fois
    
    // Attendre l'initialisation avant de rendre l'app
    if (!isInitialized) {
        return <PageLoader message="Initialisation de l'authentification..." />;
    }
    
    return (
        <AnagogiaThemeProvider>
            <Provider store={store}>
                <BrowserRouter>
                    <Suspense fallback={<PageLoader message="Chargement de l'application..." />}>
                        <Routes>
                            <Route path="/login" element={<PageDeConnexion />} />
                            <Route
                                path="/*"
                                element={
                                    <PrivateRoute>
                                        <ShellSwitch />
                                    </PrivateRoute>
                                }
                            />
                        </Routes>
                    </Suspense>
                </BrowserRouter>
            </Provider>
        </AnagogiaThemeProvider>
    );
};

export default App;